import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SERVICE_STATIC = [
  {
    catKey: 'cat1',
    icon: 'ri-building-4-line',
    image: 'https://public.readdy.ai/ai/img_res/30ba5d739728ef32bf12a526524bb9de.jpg',
  },
  {
    catKey: 'cat2',
    icon: 'ri-home-heart-line',
    image: 'https://readdy.ai/api/search-image?query=beautifully%20renovated%20residential%20living%20room%20interior%2C%20open%20floor%20plan%20with%20hardwood%20floors%2C%20modern%20white%20kitchen%20visible%20in%20background%2C%20large%20windows%20letting%20in%20natural%20daylight%2C%20neutral%20warm%20palette%20with%20light%20oak%20furniture%2C%20no%20people%2C%20photorealistic%20interior%20design%20photography%2C%20airy%20bright%20spacious%20atmosphere%2C%20ultra%20detailed&width=700&height=500&seq=svc-int-resi002&orientation=landscape',
  },
  {
    catKey: 'cat3',
    icon: 'ri-store-2-line',
    image: 'https://readdy.ai/api/search-image?query=upscale%20commercial%20office%20lobby%20interior%20renovation%2C%20polished%20marble%20floor%2C%20custom%20reception%20desk%2C%20high%20ceiling%20with%20decorative%20lighting%2C%20warm%20ambient%20glow%2C%20modern%20corporate%20aesthetic%20with%20natural%20stone%20accent%20wall%2C%20no%20people%2C%20photorealistic%20interior%20architecture%20photography%2C%20premium%20materials%2C%20sharp%20detailed&width=700&height=500&seq=svc-int-comm003&orientation=landscape',
  },
  {
    catKey: 'cat4',
    icon: 'ri-hammer-line',
    image: 'https://readdy.ai/api/search-image?query=luxury%20bathroom%20interior%20renovation%2C%20large%20format%20porcelain%20tile%20floor%2C%20frameless%20glass%20shower%20with%20brushed%20gold%20fixtures%2C%20freestanding%20soaking%20tub%2C%20warm%20vanity%20lighting%2C%20natural%20stone%20countertop%2C%20no%20people%2C%20photorealistic%20interior%20design%20photography%2C%20premium%20spa-like%20atmosphere%2C%20ultra%20sharp%20detail&width=700&height=500&seq=svc-int-spec004&orientation=landscape',
  },
];

type CatKey = 'cat1' | 'cat2' | 'cat3' | 'cat4';

export default function Services() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const active = SERVICE_STATIC[activeTab];
  const ck = active.catKey as CatKey;

  const svcItems = [
    t(`svc.${ck}.svc1`), t(`svc.${ck}.svc2`), t(`svc.${ck}.svc3`),
    t(`svc.${ck}.svc4`), t(`svc.${ck}.svc5`), t(`svc.${ck}.svc6`),
  ];

  const svcIcons = {
    cat1: ['ri-file-user-line', 'ri-money-dollar-circle-line', 'ri-tools-line', 'ri-team-line', 'ri-search-eye-line', 'ri-customer-service-2-line'],
    cat2: ['ri-home-gear-line', 'ri-paint-brush-line', 'ri-layout-4-line', 'ri-brush-line', 'ri-building-line', 'ri-add-box-line'],
    cat3: ['ri-store-line', 'ri-building-4-line', 'ri-wheelchair-line', 'ri-organization-chart', 'ri-drop-line', 'ri-cup-line'],
    cat4: ['ri-water-flash-line', 'ri-flashlight-line', 'ri-temp-cold-line', 'ri-scissors-cut-line', 'ri-water-percent-line', 'ri-plant-line'],
  };

  return (
    <section id="services" className="py-16 md:py-24 bg-[#F0F7FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="mb-10 md:mb-12">
          <span className="text-[#05294A] text-xs tracking-widest uppercase font-semibold">
            {t('svc.label')}
          </span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mt-3">
            <h3 className="text-3xl md:text-5xl font-bold text-slate-900 leading-tight">
              {t('svc.heading')} <span className="text-[#05294A]">{t('svc.headingEm')}</span>
            </h3>
            <p className="text-slate-500 text-base md:max-w-md md:text-right leading-relaxed">
              {t('svc.desc')}
            </p>
          </div>
        </div>

        {/* Mobile: 2x2 grid / Desktop: horizontal flex */}
        <div className="mb-8 md:mb-10">
          {/* Mobile 2x2 grid */}
          <div className="grid grid-cols-2 gap-2 p-1.5 bg-[#F0F7FA] md:hidden">
            {SERVICE_STATIC.map((cat, idx) => (
              <button
                key={cat.catKey}
                onClick={() => setActiveTab(idx)}
                style={activeTab === idx ? { background: '#67CBFF', color: '#05294A' } : {}}
                className={`flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs font-semibold transition-all duration-300 cursor-pointer text-left w-full ${
                  activeTab === idx
                    ? 'shadow-md'
                    : 'bg-white border border-slate-200 text-slate-500 hover:border-[#92D7E8] hover:text-slate-800'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                  <i className={`${cat.icon} text-sm`} />
                </div>
                <span className="leading-snug">{t(`svc.${cat.catKey as CatKey}.name`)}</span>
              </button>
            ))}
          </div>

          {/* Desktop horizontal row */}
          <div className="hidden md:flex gap-2 p-1.5 bg-[#F0F7FA] w-fit">
            {SERVICE_STATIC.map((cat, idx) => (
              <button
                key={cat.catKey}
                onClick={() => setActiveTab(idx)}
                style={activeTab === idx ? { background: '#67CBFF', color: '#05294A' } : {}}
                className={`flex items-center gap-2.5 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === idx
                    ? 'shadow-md'
                    : 'bg-white border border-slate-200 text-slate-500 hover:border-[#92D7E8] hover:text-slate-800'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${cat.icon} text-base`} />
                </div>
                <span>{t(`svc.${cat.catKey as CatKey}.name`)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div className="order-2 lg:order-1">
            <h4 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">{t(`svc.${ck}.name`)}</h4>
            <p className="text-slate-500 text-base leading-relaxed mb-6 md:mb-8">{t(`svc.${ck}.desc`)}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {svcItems.map((name, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 md:p-4 bg-white border border-slate-200 hover:border-[#92D7E8] hover:bg-[#B8E2E8]/20 transition-all duration-200 group"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-[#B8E2E8] text-[#05294A] group-hover:bg-[#05294A] group-hover:text-white transition-all duration-200 shrink-0">
                    <i className={`${svcIcons[ck][i]} text-sm`} />
                  </div>
                  <span className="text-slate-700 text-sm font-medium leading-snug">{name}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 md:mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <button
                onClick={() => {
                  const el = document.querySelector('#contact');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex items-center gap-2 px-6 py-3 bg-[#05294A] text-white rounded-md text-sm font-semibold hover:bg-[#032240] transition-all duration-300 whitespace-nowrap cursor-pointer group"
              >
                <span>{t('svc.btnEstimate')}</span>
                <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <Link
                to="/portfolio"
                className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-700 rounded-md text-sm font-semibold hover:border-[#67CBFF] hover:text-[#05294A] transition-all duration-300 whitespace-nowrap cursor-pointer group"
              >
                <i className="ri-image-line" />
                <span>{t('svc.btnPortfolio')}</span>
              </Link>
            </div>
          </div>

          <div className="relative overflow-hidden h-[260px] md:h-[420px] order-1 lg:order-2">
            <img
              src={active.image}
              alt={t(`svc.${ck}.name`)}
              className="w-full h-full object-cover object-top transition-all duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
            <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6 right-4 md:right-6">
              <div className="flex items-center gap-3 px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20">
                <div className="w-8 h-8 flex items-center justify-center bg-[#05294A] text-white shrink-0">
                  <i className={`${active.icon} text-sm`} />
                </div>
                <span className="text-white font-semibold text-sm">{t(`svc.${ck}.name`)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
